import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import { fetchBoundary, fetchNetwork } from '../api/participation-api';
import { ContributionModal } from './ContributionModal';
import type { MapSelection } from './MapPicker';
import { resolveStreetSegmentLabel, streetSubLabel } from '../utils/street-segment-label';
import '../participation.css';

const MAPTILER_KEY = (import.meta.env.VITE_MAPTILER_API_KEY as string | undefined) ?? '';

const BASEMAPS = [
  { key: 'streets',   label: 'Calles',   url: `https://api.maptiler.com/maps/streets-v2/style.json?key=${MAPTILER_KEY}` },
  { key: 'satellite', label: 'Satélite', url: `https://api.maptiler.com/maps/hybrid/style.json?key=${MAPTILER_KEY}` },
  { key: 'light',     label: 'Claro',    url: `https://api.maptiler.com/maps/dataviz-light/style.json?key=${MAPTILER_KEY}` },
  { key: 'dark',      label: 'Oscuro',   url: `https://api.maptiler.com/maps/dataviz-dark/style.json?key=${MAPTILER_KEY}` },
] as const;

type BasemapKey = (typeof BASEMAPS)[number]['key'];

// Per-basemap colors — tuned for contrast on each background
const NETWORK_STYLE: Record<BasemapKey, {
  line: string;
  lineOpacity: number;
  hover: string;
  selected: string;
}> = {
  streets:   { line: '#3b82f6', lineOpacity: 0.72, hover: '#1d4ed8', selected: '#1a1a2e' },
  light:     { line: '#3b82f6', lineOpacity: 0.80, hover: '#1d4ed8', selected: '#1a1a2e' },
  satellite: { line: '#7dd3fc', lineOpacity: 0.90, hover: '#38bdf8', selected: '#e0f9ff' },
  dark:      { line: '#93c5fd', lineOpacity: 0.90, hover: '#60a5fa', selected: '#e0f2fe' },
};

const ROAD_CLASS_LABEL: Record<string, string> = {
  motorway:       'Autopista',
  primary:        'Avenida principal',
  secondary:      'Calle secundaria',
  tertiary:       'Calle terciaria',
  residential:    'Calle residencial',
  service:        'Vía de servicio',
  unclassified:   'Calle sin clasificar',
  footway:        'Sendero peatonal',
  path:           'Camino',
  cycleway:       'Ciclovía',
  living_street:  'Calle compartida',
  motorway_link:  'Enlace de autopista',
  primary_link:   'Enlace de avenida',
  secondary_link: 'Enlace secundario',
  tertiary_link:  'Enlace terciario',
};

interface EdgeProps {
  id: string;
  road_class?: string | null;
  display_name?: string | null;
  osm_name?: string | null;
  street_name_resolved?: string | null;
  naming_status?: string | null;
  from_street_candidates?: string[];
  to_street_candidates?: string[];
}

function roadClassLabel(p: EdgeProps): string {
  const cls = p.road_class;
  return cls ? (ROAD_CLASS_LABEL[cls] ?? cls) : 'Vía urbana';
}

// Build feature-state-driven paint for network-line.
// hover/selected are toggled via map.setFeatureState — no setFilter, no layer stack.
// All transitions at 0ms so there is zero visual lag between state changes.
//
// MapLibre GL JS constraint: 'zoom' must be the direct input of a top-level
// interpolate/step — it cannot be nested inside 'case'. Invert the hierarchy:
// interpolate at top level, case expressions as stop output values.
function buildNetworkPaint(ns: (typeof NETWORK_STYLE)[BasemapKey]): maplibregl.LinePaint {
  return {
    'line-color': [
      'case',
      ['boolean', ['feature-state', 'selected'], false], ns.selected,
      ['boolean', ['feature-state', 'hover'],    false], ns.hover,
      ns.line,
    ],
    'line-color-transition': { duration: 0, delay: 0 },

    // zoom-driven width with per-state overrides at each stop
    'line-width': [
      'interpolate', ['linear'], ['zoom'],
      11, ['case',
        ['boolean', ['feature-state', 'selected'], false], 3,
        ['boolean', ['feature-state', 'hover'],    false], 2.5,
        1.5,
      ],
      15, ['case',
        ['boolean', ['feature-state', 'selected'], false], 7,
        ['boolean', ['feature-state', 'hover'],    false], 5,
        3,
      ],
      17, ['case',
        ['boolean', ['feature-state', 'selected'], false], 9,
        ['boolean', ['feature-state', 'hover'],    false], 7,
        5,
      ],
    ],
    'line-width-transition': { duration: 0, delay: 0 },

    'line-opacity': [
      'case',
      ['boolean', ['feature-state', 'selected'], false], 1.0,
      ['boolean', ['feature-state', 'hover'],    false], 0.95,
      ns.lineOpacity,
    ],
    'line-opacity-transition': { duration: 0, delay: 0 },
  } as unknown as maplibregl.LinePaint;
}

interface MapSectionProps {
  slug: string;
  onContributionSuccess: () => void;
  sectionRef: React.RefObject<HTMLElement>;
}

export function MapSection({ slug, onContributionSuccess, sectionRef }: MapSectionProps): JSX.Element {
  const containerRef    = useRef<HTMLDivElement>(null);
  const mapRef          = useRef<maplibregl.Map | null>(null);
  const tooltipRef      = useRef<HTMLDivElement>(null);
  const boundaryDataRef = useRef<GeoJSON.Feature | null>(null);
  const networkDataRef  = useRef<GeoJSON.FeatureCollection | null>(null);
  const selectedEdgeRef = useRef<string>('');
  const hoveredEdgeRef  = useRef<string>('');
  const basemapRef      = useRef<BasemapKey>('streets');

  const [basemap,      setBasemap]      = useState<BasemapKey>('streets');
  const [status,       setStatus]       = useState<'loading' | 'ready' | 'error'>('loading');
  const [selection,    setSelection]    = useState<MapSelection | null>(null);
  const [showPanel,    setShowPanel]    = useState(false);
  const [successCount, setSuccessCount] = useState(0);

  useEffect(() => { basemapRef.current = basemap; }, [basemap]);

  // ── Layer injection (idempotent) ──────────────────────────────────────────
  // Uses promoteId:'id' so each feature has a stable ID for setFeatureState.
  // A single layer (network-line) drives normal / hover / selected appearance
  // via feature-state paint expressions — no filter-based overlay layers.

  function addProjectLayers(map: maplibregl.Map, bk: BasemapKey): void {
    const boundary = boundaryDataRef.current;
    const network  = networkDataRef.current;
    if (!boundary || !network || !map.isStyleLoaded()) return;

    const ns = NETWORK_STYLE[bk];
    const paint = buildNetworkPaint(ns);

    try {
      // ── Boundary ──────────────────────────────────────────────────────────
      if (!map.getSource('boundary')) {
        map.addSource('boundary', { type: 'geojson', data: boundary });
      }
      if (!map.getLayer('boundary-fill')) {
        map.addLayer({ id: 'boundary-fill', type: 'fill', source: 'boundary',
          paint: { 'fill-color': '#1a1a2e', 'fill-opacity': 0.04 } });
      }
      if (!map.getLayer('boundary-line')) {
        map.addLayer({ id: 'boundary-line', type: 'line', source: 'boundary',
          paint: { 'line-color': '#1a1a2e', 'line-width': 2, 'line-dasharray': [4, 3] } });
      }

      // ── Network ───────────────────────────────────────────────────────────
      // promoteId maps the 'id' GeoJSON property to MapLibre's internal feature ID,
      // which is required for setFeatureState to work on specific features.
      if (!map.getSource('network')) {
        map.addSource('network', {
          type: 'geojson',
          data: network,
          promoteId: 'id',
        });
      }
      if (!map.getLayer('network-line')) {
        map.addLayer({ id: 'network-line', type: 'line', source: 'network', paint });
      } else {
        // After a basemap switch the layer survives only if it was somehow preserved;
        // update the paint expressions to reflect the new basemap colors.
        map.setPaintProperty('network-line', 'line-color',   paint['line-color'] as unknown);
        map.setPaintProperty('network-line', 'line-opacity', paint['line-opacity'] as unknown);
      }

      // Ensure project layers sit on top of the basemap
      try { map.moveLayer('boundary-fill'); } catch { /* already last */ }
      try { map.moveLayer('boundary-line'); } catch { /* already last */ }
      try { map.moveLayer('network-line');  } catch { /* already last */ }
    } catch {
      // Swallow — map may be in transient state during style transition
    }
  }

  // ── Map initialization ────────────────────────────────────────────────────

  useEffect(() => {
    if (!containerRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: BASEMAPS[0].url,
      center: [-92.6376, 16.7370],
      zoom: 13,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl(), 'top-right');

    map.on('load', async () => {
      try {
        const [boundary, network] = await Promise.all([
          fetchBoundary(slug),
          fetchNetwork(slug),
        ]);

        boundaryDataRef.current = boundary as unknown as GeoJSON.Feature;
        networkDataRef.current  = network as unknown as GeoJSON.FeatureCollection;

        addProjectLayers(map, 'streets');

        const canvas = map.getCanvas(); // cached — avoids per-event method call

        // ── Hover (feature-state) ──────────────────────────────────────────
        // setFeatureState is a direct GPU state update — no filter scan over
        // all 10k+ features, no layer re-render. Near-zero latency.
        map.on('mousemove', 'network-line', e => {
          const feature = e.features?.[0];
          if (!feature) return;
          const p = feature.properties as unknown as EdgeProps;
          const next = p.id;

          // Position + cursor: cheap on every pixel
          canvas.style.cursor = 'pointer';
          if (tooltipRef.current) {
            tooltipRef.current.style.display = 'flex';
            tooltipRef.current.style.left    = `${e.point.x}px`;
            tooltipRef.current.style.top     = `${e.point.y}px`;
          }

          // State + content: only when edge boundary is crossed
          if (next === hoveredEdgeRef.current) return;
          const prev = hoveredEdgeRef.current;
          hoveredEdgeRef.current = next;

          if (prev) map.setFeatureState({ source: 'network', id: prev }, { hover: false });
          map.setFeatureState({ source: 'network', id: next }, { hover: true });

          if (tooltipRef.current) {
            const hoverLabel = resolveStreetSegmentLabel(p);
            const ttName = hoverLabel.primaryName ?? 'Tramo seleccionado';
            const ttSub  = hoverLabel.primaryName
              ? `Tipo: ${roadClassLabel(p).toLowerCase()}`
              : `Tipo: ${roadClassLabel(p).toLowerCase()}`;
            tooltipRef.current.innerHTML = `
              <span class="tt-name">${ttName}</span>
              <span class="tt-sub">${ttSub}</span>
            `;
          }
        });

        map.on('mouseleave', 'network-line', () => {
          if (hoveredEdgeRef.current) {
            map.setFeatureState({ source: 'network', id: hoveredEdgeRef.current }, { hover: false });
            hoveredEdgeRef.current = '';
          }
          canvas.style.cursor = '';
          if (tooltipRef.current) tooltipRef.current.style.display = 'none';
        });

        // ── Click → panel ──────────────────────────────────────────────────
        map.on('click', 'network-line', e => {
          const feature = e.features?.[0];
          if (!feature) return;
          const p = feature.properties as unknown as EdgeProps;
          const next = p.id;
          const prev = selectedEdgeRef.current;

          if (prev && prev !== next) {
            map.setFeatureState({ source: 'network', id: prev }, { selected: false });
          }
          map.setFeatureState({ source: 'network', id: next }, { selected: true });
          selectedEdgeRef.current = next;

          const streetLabel = resolveStreetSegmentLabel(p);
          setSelection({
            geometry: feature.geometry as unknown as Record<string, unknown>,
            canonicalEdgeId: next,
            label:       streetLabel.displayLabel,
            sublabel:    streetSubLabel(streetLabel, p.road_class),
            streetLabel,
          });
          setShowPanel(true);
        });

        setStatus('ready');
      } catch {
        setStatus('error');
      }
    });

    return () => { map.remove(); mapRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // ── Basemap switch ─────────────────────────────────────────────────────────
  // 'style.load' is unreliable in MapLibre GL JS v4 for setStyle().
  // 'idle' + 400 ms timeout are the primary restore mechanisms.

  useEffect(() => {
    const map = mapRef.current;
    if (!map || status !== 'ready') return;

    const styleUrl = BASEMAPS.find(b => b.key === basemap)?.url;
    if (!styleUrl) return;

    const center = map.getCenter();
    const zoom   = map.getZoom();
    let restored = false;

    const restore = () => {
      if (restored) return;
      restored = true;
      map.jumpTo({ center, zoom });
      addProjectLayers(map, basemapRef.current);

      // Feature states are wiped with the source — restore selected edge
      if (selectedEdgeRef.current) {
        try {
          map.setFeatureState({ source: 'network', id: selectedEdgeRef.current }, { selected: true });
        } catch { /* source may not be ready yet — acceptable */ }
      }
      // Hover is cleared: cursor has moved since the switch
      hoveredEdgeRef.current = '';
      if (tooltipRef.current) tooltipRef.current.style.display = 'none';
    };

    map.setStyle(styleUrl);
    map.once('style.load', restore);
    map.once('idle',        restore);
    const tid = setTimeout(() => { if (map.isStyleLoaded()) restore(); }, 400);

    return () => { clearTimeout(tid); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basemap]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  function handleClosePanel(): void {
    setShowPanel(false);
    setSelection(null);
    const map = mapRef.current;
    if (map && selectedEdgeRef.current) {
      try { map.setFeatureState({ source: 'network', id: selectedEdgeRef.current }, { selected: false }); }
      catch { /* no-op if source was removed */ }
    }
    selectedEdgeRef.current = '';
  }

  function handleSuccess(): void {
    setSuccessCount(c => c + 1);
    onContributionSuccess();
  }

  function scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const mapWrapperStyle: CSSProperties = {
    position: 'relative',
    width: '100%',
    height: '72vh',
    minHeight: 460,
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 4px 32px rgba(0,0,0,0.08)',
  };

  return (
    <section ref={sectionRef as React.RefObject<HTMLElement>} style={{ background: '#f8fafc', paddingBottom: 48 }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 32px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, flexWrap: 'wrap', gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>
            Mapa de San Cristóbal de las Casas
          </h2>
          <button type="button" className="back-to-top" onClick={scrollToTop}>
            ↑ Volver a las opciones
          </button>
        </div>

        <p style={{ margin: '0 0 16px', fontSize: 13.5, color: '#6b7280', lineHeight: 1.55 }}>
          Navega el mapa, pasa el cursor sobre un tramo para resaltarlo, y haz clic para registrar tu aporte.
          {successCount > 0 && (
            <span style={{ color: '#22c55e', marginLeft: 10, fontWeight: 600 }}>
              ✓ {successCount} aporte{successCount > 1 ? 's' : ''} enviado{successCount > 1 ? 's' : ''}
            </span>
          )}
        </p>

        <div className="basemap-selector">
          {BASEMAPS.map(bm => (
            <button
              key={bm.key}
              type="button"
              className={`basemap-btn${basemap === bm.key ? ' active' : ''}`}
              onClick={() => setBasemap(bm.key)}
            >
              {bm.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 32px' }}>
        <div style={mapWrapperStyle}>
          {status === 'loading' && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9', zIndex: 10, fontSize: 14, color: '#94a3b8' }}>
              Cargando red vial…
            </div>
          )}
          {status === 'error' && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fef2f2', zIndex: 10, fontSize: 14, color: '#dc2626' }}>
              No se pudo cargar el mapa.
            </div>
          )}

          <div ref={containerRef} style={{ width: '100%', height: '100%' }} />

          <div ref={tooltipRef} className="map-tooltip" style={{ display: 'none' }} />

          {status === 'ready' && !showPanel && (
            <div style={{ position: 'absolute', bottom: 20, left: 20, zIndex: 150, pointerEvents: 'none' }}>
              <div style={{
                padding: '8px 16px', borderRadius: 10,
                background: 'rgba(255,255,255,0.92)',
                border: '1px solid #e2e8f0',
                fontSize: 12.5, color: '#6b7280', fontWeight: 500,
                backdropFilter: 'blur(4px)', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}>
                Haz clic sobre una calle para agregar tu aporte
              </div>
            </div>
          )}

          {showPanel && selection && (
            <ContributionModal
              scale="place"
              slug={slug}
              selection={selection}
              variant="panel"
              onClose={handleClosePanel}
              onSuccess={handleSuccess}
            />
          )}
        </div>
      </div>
    </section>
  );
}
